import React, { useEffect, useState } from 'react';
import InformationSection from './InformationSection';
import useCsvDownload from '../../hooks/useCsvDownload';
import ToastDownload from '../common/Toast';
import { listMetrics } from '../../services/listMetricsService';
import downloadBlack from '../../assets/icons/download_black.svg';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
// import download from '../../assets/icons/download.svg';

const MetricsResultsPage = ({ metricResults, metricId, metricItems }) => {
  const [allMetrics, setAllMetrics] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { onHandleDownloadCsvFile, downloadCsvState } = useCsvDownload();
    
  const handleDownloadCSV = () => {
    if (!metricId) {
      console.error('No metric ID available for download');
      return;
    }
    
    onHandleDownloadCsvFile(metricId);
  };

  useEffect(() => {
    localStorage.removeItem('emailSent');
    
    const loadMetrics = async () => {
      try {
        const response = await listMetrics();
        setAllMetrics(response?.metrics || []);
      } catch (error) {
        console.error('Error loading metrics:', error);
      }
    };
    
    loadMetrics();
  }, []);

  // Helper function to get metric value, checking for base keys only
  const getMetricValue = (data, metricId) => {
    if (!data || !metricId) return null;

    const baseKeys = [
      metricId,
      metricId.toLowerCase(),
      `agg_${metricId}`,
      `agg_${metricId.toLowerCase()}`
    ];
    
    // Check for base keys first
    for (const key of baseKeys) {
      if (data[key] !== undefined && data[key] !== null) {
        return data[key];
      }
    }

    return null;
  };

  const metricItemsRows = metricItems?.items || [];
  const metricResultsRows = metricResults?.rows || [];
  
  const rows = metricItemsRows.length > 0 
    ? metricItemsRows.map((item, index) => {
        const metricRow = metricResultsRows[index] || {};
        const itemMetric = item.metric || {};
        return {
          id: index + 1,
          source: item.sourceText || item.source || '',
          target: item.targetText || item.target || '',
          reference: item.referenceText || item.reference || '',
          ...itemMetric,
          ...metricRow 
        };
      })
    : metricResultsRows;

  const pageSize = 10;
  const totalRows = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedRows = rows.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    if (currentPage !== safePage) {
      setCurrentPage(safePage);
    }
  }, [currentPage, safePage]);
  
  const metricsList = metricResults?.metric?.metrics?.metrics || [];
  const metricIds = metricsList.map(metric => {
    if (typeof metric === 'string') {
      return metric.toLowerCase();
    }
    return (metric.id || metric.name || metric).toLowerCase();
  });
  
  const metricNames = metricIds.map(metricId => {
    const matchedMetric = allMetrics.find(m => 
      (m.id?.toLowerCase() || '') === metricId
    );
    return matchedMetric?.name || metricId;
  });

  const getMetricRequirements = (metricId) => {
    const matchedMetric = allMetrics.find(m =>
      (m.id?.toLowerCase() || '') === metricId
    );
    const io = matchedMetric?.io || {};
    const isRequired = (value) =>
      typeof value === 'string' ? value === 'required' : value?.required === true;
    return {
      source: isRequired(io.source),
      target: isRequired(io.target),
      reference: isRequired(io.reference),
    };
  };

  const hasText = (value) => typeof value === 'string' && value.trim().length > 0;

  const getMissingRequiredParts = (row, metricId) => {
    const requirements = getMetricRequirements(metricId);
    const missing = [];
    if (requirements.source && !hasText(row.source)) missing.push('Source');
    if (requirements.target && !hasText(row.target)) missing.push('Target');
    if (requirements.reference && !hasText(row.reference)) missing.push('Reference');
    return missing;
  };
  
  const aggregated = metricResults?.metric?.aggregated || {};
  const aggregatedValues = metricIds.map(id => {
    return getMetricValue(aggregated, id);
  });

  const getScoreBadgeClass = (value) => {
    return 'bg-[#F1F5F9] text-[#64748B]';
  };

  if (!metricResults) {
    return (
      <div className="">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">No Results Available</h2>
            <p className="text-gray-600">No metric results were found.</p>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="">
      {/* Toast for download status */}
      {false && (
        <ToastDownload
          stateName={downloadCsvState}
          title="Metrics CSV Download"
          linkAction={handleDownloadCSV}
        />
      )}
      
      {/* Metrics Evaluation Results Section */}
      <div className="mb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold">
              Metrics Evaluation Results
            </h2>
          </div>
          <div className="flex gap-2 h-10">
            <button 
              onClick={handleDownloadCSV}
              //disabled={!metricResults.result || Object.entries(metricResults.result).length === 0 || downloadCsvState === toastDownloadStateNames.isLoading} 
              className="px-4 py-2 w-full sm:w-[170px] border border-[#E2E8F0] shadow-[0px_1px_2px_0px_#0000000D]
            bg-white text-[#020817] disabled:bg-[#F9FAFB] disabled:cursor-not-allowed rounded-md hover:bg-[#F1F5F9] flex items-center gap-2">
              <img src={downloadBlack} alt="Download CSV" className="w-4 h-4" />
              Download CSV
            </button>
            {/* <button className="px-4 py-2 border bg-[#020817] shadow-[0px_1px_2px_0px_#0000000D] text-white rounded-md 
            hover:bg-[#29303D] flex items-center gap-2">            
              <img src={download} alt="Download PDF" className="w-4 h-4" />
              Download PDF
            </button> */}
          </div>
        </div>

        {/* Results Table */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-full">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                <th className="px-3 py-3 text-center text-sm font-semibold text-[#334155] w-12">#</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-[#334155] min-w-[160px]">Source</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-[#334155] min-w-[160px]">Target</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-[#334155] min-w-[160px]">Reference</th>
                {metricNames.map((name, index) => (
                  <th key={index} className="px-3 py-3 text-center text-sm font-semibold text-[#334155] max-w-[98px]">
                    {name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E2E8F0]">
              {paginatedRows.map((row) => (
                <tr
                  key={row.id}
                  className="bg-white border-b border-[#E2E8F0] hover:bg-[#F8FAFF] transition-colors duration-150"
                >
                  <td className="px-3 py-3 text-sm font-medium text-[#0F172A] w-12 text-center">{row.id}</td>
                  <td className="px-3 py-3 text-sm text-gray-900 whitespace-normal break-words min-w-[160px]">{row.source}</td>
                  <td className="px-3 py-3 text-sm text-gray-900 whitespace-normal break-words min-w-[160px]">{row.target}</td>
                  <td className="px-3 py-3 text-sm text-gray-900 whitespace-normal break-words min-w-[160px]">{row.reference}</td>
                  {metricIds.map((id, index) => {
                    // Use helper function to get metric value (checks for f1, p, r suffixes)
                    const value = getMetricValue(row, id);
                    const missingRequiredParts = getMissingRequiredParts(row, id);
                    
                    // Format to 2 decimal places
                    let formattedValue = '-';
                    let numericValue = null;
                    if (value !== undefined && value !== null) {
                      const numValue = typeof value === 'number' ? value : parseFloat(value);
                      if (!isNaN(numValue)) {
                        numericValue = numValue;
                        formattedValue = numValue.toFixed(2);
                      } else {
                        formattedValue = String(value);
                      }
                    }
                    
                    const isMissingRequired = formattedValue === '-' && missingRequiredParts.length > 0;
                    const cellContent = (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md font-medium ${
                          isMissingRequired
                            ? 'bg-[#FEE2E2] text-[#991B1B] cursor-help'
                            : getScoreBadgeClass(numericValue)
                        }`}
                      >
                        {formattedValue}
                      </span>
                    );

                    return (
                      <td key={index} className="px-3 py-3 text-sm text-gray-900 min-w-[98px] text-center">
                        {isMissingRequired ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {cellContent}
                              </TooltipTrigger>
                              <TooltipContent>
                                Missing required: {missingRequiredParts.join(', ')}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          cellContent
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0] font-semibold">
                <td className="px-3 py-3 text-sm font-semibold text-[#0F172A] w-12 text-center">Total</td>
                <td className="px-3 py-3 text-sm text-gray-500 min-w-[160px]"></td>
                <td className="px-3 py-3 text-sm text-gray-500 min-w-[160px]"></td>
                <td className="px-3 py-3 text-sm text-gray-500 min-w-[160px]"></td>
                {aggregatedValues.map((value, index) => {
                  // Format to 2 decimal places
                  let formattedValue = '-';
                  let numericValue = null;
                  if (value !== undefined && value !== null) {
                    const numValue = typeof value === 'number' ? value : parseFloat(value);
                    if (!isNaN(numValue)) {
                      numericValue = numValue;
                      formattedValue = numValue.toFixed(2);
                    } else {
                      formattedValue = String(value);
                    }
                  }
                  
                  return (
                    <td key={index} className="px-3 py-3 text-sm text-gray-900 max-w-[98px] text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md font-medium ${getScoreBadgeClass(numericValue)}`}>
                        {formattedValue}
                      </span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="md:hidden px-4 py-4 space-y-4">
          {paginatedRows.map((row) => (
            <div key={row.id} className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">#{row.id}</span>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-gray-900">
                <div>
                  <div className="text-xs uppercase tracking-wide text-[#6B7280]">Source</div>
                  <div className="whitespace-normal break-words">{row.source}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-[#6B7280]">Target</div>
                  <div className="whitespace-normal break-words">{row.target}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-[#6B7280]">Reference</div>
                  <div className="whitespace-normal break-words">{row.reference}</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {metricIds.map((id, index) => {
                    const metricName = metricNames[index];
                    // Use helper function to get metric value (checks for f1, p, r suffixes)
                    const value = getMetricValue(row, id);
                    const missingRequiredParts = getMissingRequiredParts(row, id);
                    
                    // Format to 2 decimal places
                    let formattedValue = '-';
                    if (value !== undefined && value !== null) {
                      const numValue = typeof value === 'number' ? value : parseFloat(value);
                      if (!isNaN(numValue)) {
                        formattedValue = numValue.toFixed(2);
                      } else {
                        formattedValue = String(value);
                      }
                    }
                    
                    const isMissingRequired = formattedValue === '-' && missingRequiredParts.length > 0;
                    const valueNode = (
                      <div className={isMissingRequired ? 'text-[#991B1B] font-medium' : ''}>
                        {formattedValue}
                      </div>
                    );
                    
                    return (
                      <div key={index}>
                        <div className="text-xs uppercase tracking-wide text-[#6B7280]">{metricName}</div>
                        {isMissingRequired ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {valueNode}
                              </TooltipTrigger>
                              <TooltipContent>
                                Missing required: {missingRequiredParts.join(', ')}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          valueNode
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0] bg-white">
          <div className="text-xs text-[#6B7280]">
            Showing {totalRows === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + pageSize, totalRows)} of {totalRows}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
              disabled={safePage === 1}
              className="px-3 py-1.5 text-sm border border-[#E2E8F0] rounded-md bg-white text-[#020817] disabled:text-[#9CA3AF] disabled:bg-[#F9FAFB]"
            >
              ←
            </button>
            <span className="text-xs text-[#6B7280]">
              {safePage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
              disabled={safePage === totalPages}
              className="px-3 py-1.5 text-sm border border-[#E2E8F0] rounded-md bg-white text-[#020817] disabled:text-[#9CA3AF] disabled:bg-[#F9FAFB]"
            >
             →
            </button>
          </div>
        </div>
        </div>

      </div>

       <InformationSection />
    </div>
  );
};

export default MetricsResultsPage;
